import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "@emotion/react/jsx-runtime";
import { Fragment, useEffect, useMemo, useState } from "react";
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';
import { useI18N } from "../../context/I18NContext";
import { useSettings } from "../../context/SettingsContext";
import { useFlow } from "../../context/FlowContext";
import { useNavigate } from "react-router-dom";
import useWidgetStore from "../../store/useWidgetStore";
import useDataStore from "../../store/useDataStore";
import dayjs from 'dayjs';
import 'dayjs/locale/nl';
import 'dayjs/locale/en';
import 'dayjs/locale/fr';
import 'dayjs/locale/de';
import { appointmentsInOrder, apptypeNamesFor, capacityGroupNamesFrom, capacityLabelFor, resourceNamesFor } from "../../helpers/appointment";
import CartSimple from "./CartSimple";
export default function CartSummary(props) {
    const { t, ts } = useI18N();
    const theme = useTheme();
    const navigate = useNavigate();
    const { booking, language } = useWidgetStore();
    const { apptypes, resources } = useDataStore();
    const { staticData, showSelectedResource } = useSettings();
    const flow = useFlow();
    const [rows, setRows] = useState([]);
    const isLarge = useMediaQuery(theme.breakpoints.up('sm'));
    const capacityGroupNames = useMemo(() => capacityGroupNamesFrom(flow.views), [flow.views]);
    useEffect(() => {
        var _a;
        let _rows = [];
        const formatPrice = (amount) => new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(amount).replace(',00', ',-');
        if (((_a = booking.appointments) === null || _a === void 0 ? void 0 : _a.length) > 0) {
            // Each appointment gets its own block - date, apptype, resource, capacity - read off
            // THAT appointment rather than off appointment[0] plus whichever apptype/capacity the
            // pickers currently have live-selected. Appointments in one booking can differ on all
            // of these (a second one added under a different apptype, with a different capacity),
            // so a single shared block for the whole booking either showed the wrong apptype for
            // every appointment but the first, or the wrong capacity for every one but the last.
            appointmentsInOrder(booking.appointments).forEach((one) => {
                var _a;
                _rows.push({ text: dayjs(one.date).locale(language).format('dddd D MMMM HH:mm'), bold: true });
                const apptypeNames = apptypeNamesFor(one, apptypes);
                if (apptypeNames.length > 0) {
                    _rows.push({ text: apptypeNames.join(', ') });
                }
                if (showSelectedResource) {
                    const resourceNames = resourceNamesFor(one, (_a = staticData === null || staticData === void 0 ? void 0 : staticData.resources) !== null && _a !== void 0 ? _a : []);
                    if (resourceNames.length > 0) {
                        _rows.push({ text: resourceNames.join(', ') });
                    }
                }
                const capacityText = capacityLabelFor(one, capacityGroupNames, { t, ts }, 'cartSummary');
                if (capacityText !== '') {
                    _rows.push({ text: capacityText });
                }
                if (one.price > 0) {
                    _rows.push({ text: ts('Prijs: %price%', { price: formatPrice(one.price) }, 'cartSummary') });
                }
            });
        }
        setRows(_rows);
    }, [apptypes, resources, booking, capacityGroupNames]);
    const goTo = (e) => {
        let target = e.target.closest('button');
        let path = target.getAttribute('rel');
        navigate(`/oa_widget_${path}`);
    };
    return (_jsxs(_Fragment, { children: [_jsx(TableContainer, { component: Paper, sx: {
                    marginBottom: (props === null || props === void 0 ? void 0 : props.cartSimple) ? 0 : 4,
                    borderRadius: (props === null || props === void 0 ? void 0 : props.cartSimple) ? '4px 4px 0 0' : '4px',
                }, children: _jsx(Table, { "aria-label": t('Jouw gegevens', 'cartSummary'), children: _jsx(TableBody, { sx: {
                            "& .MuiTableCell-body": {
                                fontSize: '1rem'
                            }
                        }, children: _jsxs(Fragment, { children: [_jsxs(TableRow, { sx: {
                                        backgroundColor: theme.palette.background.default
                                    }, children: [_jsx(TableCell, { colSpan: 3, children: rows === null || rows === void 0 ? void 0 : rows.map((row, i) => (_jsx(Box, { sx: Object.assign({ fontSize: '1.25rem' }, (row.bold && {
                                                    fontWeight: 700
                                                })), children: row.text }, i))) }), !props.bookingCore && isLarge && (_jsx(TableCell, { sx: { width: '2em' }, align: "right", children: _jsx(IconButton, { onClick: goTo, rel: 'index', "aria-label": "edit", size: "medium", children: _jsx(EditIcon, { sx: { color: theme.palette.text.secondary } }) }) }))] }), !props.bookingCore && !isLarge && (_jsx(TableRow, { sx: {
                                        backgroundColor: theme.palette.background.default
                                    }, children: _jsx(TableCell, { colSpan: 3, sx: { padding: 0 }, children: _jsx(Button, { fullWidth: true, onClick: goTo, rel: 'index', sx: { marginBottom: 2 }, variant: "contained", color: "secondary", children: t('Gegevens wijzigen') }) }) }))] }) }) }) }), props.cartSimple && (_jsx(_Fragment, { children: _jsx(CartSimple, { cartSummary: true }) }))] }));
}
